import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BrandsService } from '@services/data-source-tables/brands.service';
import {
  RentalTimelineService,
  RentalTimelineData,
} from '@services/data-source-tables/rentals/rental-timeline.service';
import { RentalTimelineGroup, RentalTimelineItem } from '@/models/rentals/rental-timeline.model';
import { BrandModel } from '@/models/tables/brand.model';
import { RentalReservationModel } from '@/models/rentals/rental-reservation.model';
import { DialogFormRentalReservationComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/dialog-form-rental-reservation.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Timeline, DataSet } from 'vis-timeline/standalone';

@Component({
  selector: 'app-rental-timeline',
  templateUrl: './rental-timeline.component.html',
  styleUrls: ['./rental-timeline.component.scss'],
})
export class RentalTimelineComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('timelineContainer', { static: false }) timelineContainer: ElementRef;

  filterStartDate: FormControl;
  filterEndDate: FormControl;
  filterMarcaId = new FormControl<number | null>(null);

  brands: BrandModel[] = [];
  isLoading = false;

  private timeline: Timeline;
  private groupsDataSet: DataSet<any>;
  private itemsDataSet: DataSet<any>;
  private allGroups: RentalTimelineGroup[] = [];
  private allItems: RentalTimelineItem[] = [];
  private subscriptions: Subscription[] = [];
  private reloadTrigger$ = new Subject<void>();
  private groupTooltipEl: HTMLDivElement | null = null;

  constructor(
    private router: Router,
    private rentalTimelineService: RentalTimelineService,
    private dialog: MatDialog,
  ) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inThirtyDays = new Date(today);
    inThirtyDays.setDate(inThirtyDays.getDate() + 30);

    this.filterStartDate = new FormControl(today);
    this.filterEndDate = new FormControl(inThirtyDays);
  }

  ngOnInit(): void {
    this.setupFilterSubscriptions();
    this.loadBrands();

    const reloadSub = this.reloadTrigger$.subscribe(() => this.loadData());
    this.subscriptions.push(reloadSub);
  }

  ngAfterViewInit(): void {
    this.initTimeline();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.reloadTrigger$.complete();
    this.destroyGroupTooltip();
    if (this.timeline) {
      this.timeline.destroy();
    }
  }

  refreshTimeline(): void {
    this.loadData();
  }

  openNewReservationDialog(): void {
    this.dialog.open(DialogFormRentalReservationComponent, {
      data: {
        reservation: new RentalReservationModel(),
        isNew: true,
        reloadTrigger$: this.reloadTrigger$,
      },
      width: 'auto',
      panelClass: 'custom-modalbox',
    });
  }

  goToToday(): void {
    if (!this.timeline) return;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);
    this.timeline.setWindow(start, end, {
      animation: { duration: 500, easingFunction: 'easeInOutQuad' },
    });
  }

  goToWeek(): void {
    if (!this.timeline) return;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 3);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
    this.timeline.setWindow(start, end, {
      animation: { duration: 500, easingFunction: 'easeInOutQuad' },
    });
  }

  goToMonth(): void {
    if (!this.timeline) return;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.timeline.setWindow(start, end, {
      animation: { duration: 500, easingFunction: 'easeInOutQuad' },
    });
  }

  protected initTimeline(): void {
    const container = this.timelineContainer.nativeElement;
    this.groupsDataSet = new DataSet([]);
    this.itemsDataSet = new DataSet([]);

    const start = this.filterStartDate.value ? new Date(this.filterStartDate.value) : new Date();
    const end = this.filterEndDate.value
      ? new Date(this.filterEndDate.value)
      : new Date(new Date().setDate(new Date().getDate() + 30));

    const options = {
      stack: false,
      start,
      end,
      zoomable: false,
      moveable: false,
      orientation: 'top' as const,
      verticalScroll: true,
      maxHeight: '70vh',
      margin: {
        item: { horizontal: 0, vertical: 4 },
      },
      tooltip: {
        followMouse: false,
        overflowMethod: 'cap' as const,
        delay: 150,
      },
      xss: {
        disabled: true,
      },
    };

    this.timeline = new Timeline(container, this.itemsDataSet, this.groupsDataSet, options);

    this.timeline.on('select', (properties: any) => {
      if (properties.items && properties.items.length > 0) {
        const itemId = properties.items[0];
        const item = this.allItems.find((i) => i.id === itemId);
        if (item) {
          this.openItemDetail(item);
        }
      }
    });

    this.setupGroupTooltip();
  }

  private setupGroupTooltip(): void {
    if (!this.groupTooltipEl) {
      const el = document.createElement('div');
      el.className = 'vis-tooltip group-vis-tooltip';
      el.style.display = 'none';
      el.style.visibility = 'visible';
      el.style.position = 'fixed';
      el.style.pointerEvents = 'none';
      el.style.zIndex = '10000';
      const host = this.timelineContainer?.nativeElement?.parentElement ?? document.body;
      host.appendChild(el);
      this.groupTooltipEl = el;
    }

    this.timeline.on('mouseOver', (properties: any) => {
      if (!properties || properties.what !== 'group-label' || properties.group == null) {
        this.hideGroupTooltip();
        return;
      }

      const group = this.allGroups.find((g) => String(g.id) === String(properties.group));
      if (!group?.tooltipHtml) {
        this.hideGroupTooltip();
        return;
      }

      this.showGroupTooltip(group.tooltipHtml, properties.event);
    });

    this.timeline.on('mouseMove', (properties: any) => {
      if (!properties || properties.what !== 'group-label') {
        this.hideGroupTooltip();
      } else if (this.groupTooltipEl && properties.event) {
        this.positionTooltip(properties.event);
      }
    });
  }

  private showGroupTooltip(html: string, event: MouseEvent | undefined): void {
    if (!this.groupTooltipEl || !event) return;
    this.groupTooltipEl.innerHTML = html;
    this.groupTooltipEl.style.display = 'block';
    this.positionTooltip(event);
  }

  private positionTooltip(event: MouseEvent): void {
    if (!this.groupTooltipEl) return;
    this.groupTooltipEl.style.left = `${event.clientX + 14}px`;
    this.groupTooltipEl.style.top = `${event.clientY + 14}px`;
  }

  private hideGroupTooltip(): void {
    if (this.groupTooltipEl) {
      this.groupTooltipEl.style.display = 'none';
    }
  }

  private destroyGroupTooltip(): void {
    if (this.groupTooltipEl?.parentNode) {
      this.groupTooltipEl.parentNode.removeChild(this.groupTooltipEl);
    }
    this.groupTooltipEl = null;
  }

  protected openItemDetail(item: RentalTimelineItem): void {
    const targetId = item.dettaglioId ?? item.practiceId;
    if (targetId == null) return;

    switch (item.tipo) {
      case 'NOLEGGIO_PRATICA':
        this.router.navigate(['/rental-practices/detail-practice', targetId]);
        return;
      case 'PRENOTAZIONE_PRATICA':
      case 'PRENOTAZIONE_LIBERA':
        this.router.navigate(['/rental-practices/reservations'], {
          state: { editReservationId: targetId },
        });
        return;
      default:
        this.router.navigate(['/rental-practices/detail-practice', targetId]);
    }
  }

  protected loadBrands(): void {
    const sub = this.rentalTimelineService.getBrands().subscribe({
      next: (brands: BrandModel[]) => {
        this.brands = brands ?? [];

        if (this.filterMarcaId.value == null && this.brands.length > 0) {
          this.filterMarcaId.setValue(this.brands[0].id, { emitEvent: false });
        }

        this.loadData();
      },
      error: () => {
        this.brands = [];
      },
    });

    this.subscriptions.push(sub);
  }

  protected loadData(): void {
    const marcaId = this.filterMarcaId.value;
    const startValue = this.filterStartDate.value;
    const endValue = this.filterEndDate.value;

    if (marcaId == null || startValue == null || endValue == null) {
      return;
    }

    this.isLoading = true;

    const sub = this.rentalTimelineService
      .getTimeline({
        dataInizio: this.formatDate(startValue),
        dataFine: this.formatDate(endValue),
        marcaId,
      })
      .subscribe({
        next: (data: RentalTimelineData) => {
          this.allGroups = data.groups;
          this.allItems = data.items;
          this.renderDataSets();
          this.isLoading = false;
        },
        error: () => {
          this.allGroups = [];
          this.allItems = [];
          this.renderDataSets();
          this.isLoading = false;
        },
      });

    this.subscriptions.push(sub);
  }

  protected setupFilterSubscriptions(): void {
    const serverSideControls: AbstractControl[] = [
      this.filterStartDate,
      this.filterEndDate,
      this.filterMarcaId,
    ];

    serverSideControls.forEach((control) => {
      const sub = control.valueChanges.pipe(debounceTime(400)).subscribe(() => this.loadData());
      this.subscriptions.push(sub);
    });
  }

  protected renderDataSets(): void {
    if (this.groupsDataSet && this.itemsDataSet) {
      this.groupsDataSet.clear();
      this.itemsDataSet.clear();
      this.groupsDataSet.add(this.allGroups as any[]);
      this.itemsDataSet.add(this.allItems as any[]);
    }

    if (this.timeline && this.filterStartDate.value && this.filterEndDate.value) {
      this.timeline.setWindow(
        new Date(this.filterStartDate.value),
        new Date(this.filterEndDate.value),
      );
    }
  }

  private formatDate(value: Date | string): string {
    const d = value instanceof Date ? value : new Date(value);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
